import { useCallback } from "react";
import { NavigateOptions, useNavigate, useParams } from "react-router-dom";
import { Subdomain, useSubDomain } from "../../hooks/useSubDomain";

type AppPathParams = {
  orgId?: string;
  requestId?: string;
};

export const useAppNavigate = (subdomain?: Subdomain) => {
  const validSubdomain = useSubDomain(subdomain);
  if (subdomain && !validSubdomain) {
    console.error(`Invalid subdomain ${subdomain} in useAppNavigate`);
  }

  const pathParams = useParams<AppPathParams>();
  const navigate = useNavigate();

  const root = subdomain
    ? `${window.location.origin.replace("//", `//${subdomain}.`)}/`
    : "/";

  const toLandingPage = useCallback(() => {
    return {
      path: root,
      navigate: (options?: NavigateOptions) => navigate(root, options)
    };
  }, [navigate, root]);

  const toOrganisation = useCallback(() => {
    return {
      list: () => {
        const path = `/organisations`;
        return {
          path,
          navigate: (options?: NavigateOptions) => navigate(path, options)
        };
      }
    };
  }, [navigate]);

  const toRequest = useCallback(
    (paramsOverride?: AppPathParams) => {
      const { orgId } = { ...pathParams, ...paramsOverride };
      return {
        list: () => {
          validatePropsPresent({ orgId }, ["orgId"]);
          const path = `${root}organisations/${orgId}/requests`;
          return {
            path,
            navigate: (options?: NavigateOptions) => navigate(path, options)
          };
        },
        detail: (requestId: string) => {
          validatePropsPresent({ orgId, requestId }, ["orgId", "requestId"]);
          const path = `${root}organisations/${orgId}/requests/${requestId}`;
          return {
            path,
            navigate: (options?: NavigateOptions) => navigate(path, options)
          };
        }
      };
    },
    [navigate, pathParams, root]
  );

  return { toOrganisation, toRequest, toLandingPage };
};

const validatePropsPresent = (
  props: AppPathParams,
  required: (keyof AppPathParams)[]
) => {
  const missing = required.filter((key) => !props[key]);
  if (missing.length) {
    throw new Error(
      `Missing required prop(s) ${missing.join(", ")} in useAppNavigate`
    );
  }
};
